import { NavSectionKey } from './navSections';
import { Permissions } from './permissions';

type PermissionSectionId = keyof typeof Permissions;

/**
 * Mapping: which Permission ACL sections are unlocked when a nav section
 * is set to 'enabled' for an organisation. Used by the admin UI to
 * auto-sync the org's bitmap when visibility changes — flipping a
 * section to Enabled grants the mapped permissions in full, flipping
 * it to Hidden / Preview clears them.
 *
 * Sections without a mapped permission entry (most of governance,
 * training, marketplace navigation, group parents, etc.) don't carry
 * separate ACL bits — visibility alone gates them.
 *
 * Several nav sections share a permission section (e.g. business
 * services, business functions, enabling services and assets all touch
 * the `asset` permission). That's fine: the auto-sync OR-s the bits
 * together, so the perm stays granted as long as any of the mapped
 * sections is enabled.
 */
export const navSectionToPermissions: Partial<Record<NavSectionKey, PermissionSectionId[]>> = {
  // Organisation
  'organisation.overview': ['organisation_context'],
  'organisation.businessServices': [
    'asset',
    'asset_owner',
    'documents',
    'connection',
    'connection_asset'
  ],
  'organisation.businessFunctions': [
    'asset',
    'asset_owner',
    'documents',
    'connection',
    'connection_asset'
  ],
  'organisation.enablingServices': [
    'asset',
    'asset_owner',
    'documents',
    'connection',
    'connection_asset'
  ],
  'organisation.assets': [
    'asset',
    'asset_owner',
    'documents',
    'connection',
    'connection_asset',
    'connection_asset_measure'
  ],
  'organisation.users': ['user', 'user_roles'],
  // Customers, suppliers and interested parties are all `Asset`
  // (people#*) subtypes, gated by the same `asset` family as the
  // structure pages above — they have no separate ACL of their own.
  'organisation.customers': ['asset', 'asset_owner', 'documents'],
  'organisation.suppliers': ['asset', 'asset_owner', 'documents'],
  'organisation.interestedParties': ['asset', 'asset_owner', 'documents'],

  // Governance
  // Goals, security projects and exceptions are stored as assets but
  // carry their own ACL sections. RASCI and asset classifications are
  // taxonomy/responsibility views over the asset register and stay on
  // `asset`. The access matrix manages role↔asset access and rides on
  // `settings_RolesAndPermissions`; the compliance calendar surfaces
  // framework obligations and rides on `settings_frameworks`.
  'governance.goals': ['goal', 'asset_owner', 'documents'],
  'governance.securityProjects': ['securityProject', 'asset_owner', 'documents'],
  'governance.kpis': ['kpi', 'kpi_values'],
  'governance.rasci': ['asset'],
  'governance.accessMatrix': ['settings_RolesAndPermissions'],
  'governance.complianceCalendar': ['settings_frameworks'],
  'governance.assetCategoryClassification': ['asset'],
  'governance.exceptions': ['exception', 'asset_owner', 'documents'],

  // Risk
  // `settings_bia` lives alongside `bia` here because the BIA settings
  // pages (tolerance levels, impact criteria, etc.) are reached from
  // the BIA section, not from the global Settings nav.
  'risk.bia': ['bia', 'settings_bia'],
  'risk.risks': [
    'risk',
    'risk_owner',
    'risk_assessor',
    'risk_committee',
    'risk_asset',
    'risk_threat',
    'risk_vulnerability',
    'risk_measure'
  ],
  'risk.threats': ['threat'],
  'risk.vulnerabilities': ['vulnerability'],
  'risk.measures': ['measure'],

  // Incidents
  // Incident records & problems share the `eventsAndIncidents` ACL;
  // scenarios & playbooks are stored as assets but carry their own
  // `incidentScenario` / `playbook` sections.
  'incidents.incidents': ['eventsAndIncidents'],
  'incidents.scenarios': ['incidentScenario', 'asset_owner', 'documents'],
  'incidents.playbooks': ['playbook', 'asset_owner', 'documents'],
  'incidents.problems': ['eventsAndIncidents'],

  // Disaster Recovery
  // All three sub-pages share the existing `disasterRecovery` ACL,
  // enforced server-side in drpTestResolvers.
  'disasterRecovery.tests': ['disasterRecovery'],
  'disasterRecovery.scenarios': ['disasterRecovery'],
  'disasterRecovery.checklists': ['disasterRecovery'],

  // Compliance
  'compliance.gapAnalysis': ['settings_frameworks'],
  'compliance.soa': ['settings_frameworks'],
  'compliance.assessments': ['assessments'],
  'compliance.systemKpis': ['kpi'],
  // Policy library lists policy assets (own `policy` section) and their
  // attached documents.
  'compliance.policyLibrary': ['policy', 'documents'],
  'compliance.frameworkUcf': ['settings_frameworks'],
  'compliance.frameworkDora': ['settings_frameworks'],
  'compliance.frameworkGdpr': ['settings_frameworks'],
  'compliance.frameworkPciDss': ['settings_frameworks'],
  'compliance.frameworkLaw95': ['settings_frameworks'],
  'compliance.frameworkLaw69': ['settings_frameworks'],
  'compliance.frameworkIso27001': ['settings_frameworks'],

  // Time tracking
  // All five sub-pages share the single `timetracking` ACL; the action
  // bits inside it draw the personal / manager / admin lines, so visibility
  // of any sub-page unlocks the same permission section.
  'timetracking.myTimesheet': ['timetracking'],
  'timetracking.holidays': ['timetracking'],
  'timetracking.reports': ['timetracking'],
  'timetracking.exports': ['timetracking'],
  'timetracking.projects': ['timetracking'],

  // Training
  // Videos & campaigns are asset-backed but carry their own ACL sections.
  'training.videos': ['video'],
  'training.campaigns': ['campaign'],

  // Marketplace
  marketplace: ['gen360_marketplace'],
  'marketplace.gen360': ['gen360_marketplace'],

  // Settings
  'settings.organisation': ['settings_organisation'],
  'settings.tree': ['settings_corporateTree'],
  'settings.functions': ['settings_functions'],
  // Email templates, ID numbering, branding, connectors, sensors and
  // billing are all org-settings surfaces gated by `settings_organisation`.
  'settings.emailTemplates': ['settings_organisation'],
  'settings.idNumbering': ['settings_organisation'],
  'settings.branding': ['settings_organisation'],
  'settings.connectors': ['settings_organisation'],
  'settings.sensors': ['settings_organisation'],
  'settings.billing': ['settings_organisation'],
  'settings.importExport': ['import_export'],
  'settings.accessValidation': ['access_validation'],
  // Team-members nav also unlocks the org-level roles & permissions
  // admin surface — there's no dedicated nav slot for it, it's
  // accessed from within the team / member management flow.
  'settings.team': [
    'settings_teamMembers',
    'settings_RolesAndPermissions',
    'settings_RolesAndPermissions_organisationRoles',
    'settings_RolesAndPermissions_specialGroups'
  ],
  'settings.invites': ['settings_invites'],
  'settings.sso': ['settings_ssoAndProvisioning']
};

export function getMappedPermissionsForNavSection(section: NavSectionKey): PermissionSectionId[] {
  return navSectionToPermissions[section] ?? [];
}
